/**
 * The indicative treatment for a Gemeinde whose pack is a draft.
 *
 * One component, used on every surface a Gemeinde name appears on — overview,
 * dossier intake, run, report. That is the point: if the label lived in four
 * places it would eventually disagree with itself, and a draft that reads as
 * verified on one screen is the single failure this milestone must not have.
 *
 * The treatment is M1's, unchanged: dashed border, grey, `Badge variant
 * ="indicative"`. Not one signal token is in reach here — the badge component
 * has no red/amber/green variant at all, so a draft cannot pick up a traffic
 * light by accident, and a test asserts the rendered markup carries no
 * `signal-` class.
 */
import { CircleDashed, FileClock } from 'lucide-react'

import { Icon } from '@/components/Icon'
import { Badge } from '@/components/ui/badge'
import { useI18n } from '@/i18n'

/** The compact form: sits next to a Gemeinde name in a list or a header. */
export function DraftBadge({ className }: { className?: string }) {
  const { t } = useI18n()
  return (
    <Badge variant="indicative" className={className} data-testid="draft-badge">
      <Icon icon={FileClock} />
      {t('rulebase.draftBadge')}
    </Badge>
  )
}

/**
 * The full sentence, for surfaces where the reader is about to act on the
 * Gemeinde (intake, run, report) rather than just scanning a list.
 *
 * `message` is the backend's own sentence and always wins when it is there.
 * That matters more than it looks: the honest wording differs between "nothing
 * is confirmed yet, everything is indicative" and "1 of 2 zone rows are
 * confirmed, only what is confirmed produces a hard finding" — and only the
 * server knows which of those is true. Composing that sentence here would mean
 * guessing, and the second state is exactly where a guess would be wrong.
 */
export function DraftNote({
  gemeinde,
  message,
  className,
}: {
  gemeinde?: string | null
  /** The backend's own wording for this state — rendered verbatim. */
  message?: string | null
  className?: string
}) {
  const { t } = useI18n()
  return (
    <div
      role="note"
      data-testid="draft-note"
      className={[
        'flex items-start gap-3 rounded-sm border border-dashed border-border-strong bg-surface-2 px-4 py-3 text-[13px] text-muted',
        className ?? '',
      ].join(' ')}
    >
      <Icon icon={FileClock} className="mt-0.5" />
      <span>
        {gemeinde ? <span className="font-medium text-ink-2">{gemeinde}: </span> : null}
        {message ?? t('rulebase.draftNote')}
      </span>
    </div>
  )
}

/**
 * "n offen" — the open remainder of a pack, next to the verified badge.
 *
 * It exists because a card that says «verifiziert» and «4 offen» in two
 * different places reads as a contradiction. The verified badge now carries its
 * own fraction, and this one carries the remainder in the SAME line and the
 * same weight, so the two numbers are read together instead of one being read
 * as a verdict and the other as a footnote.
 *
 * `outline`, never a signal colour: an open rule is work, not a traffic light,
 * and the pack is still a verified pack.
 */
export function OpenCountBadge({ count, className }: { count: number; className?: string }) {
  const { t } = useI18n()
  return (
    <Badge variant="outline" className={className} data-testid="open-count-badge" data-open={count}>
      <Icon icon={CircleDashed} />
      {t('rulebase.openCountBadge', { count })}
    </Badge>
  )
}
